import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaArrowRight, FaCheckCircle, FaEnvelope, FaEye, FaEyeSlash, FaLock, FaShieldAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import api, { initialiseCsrf } from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { normalizeRole, getDashboardPathForRole } from "../utils/authRole";
import loginIllustration from "../assets/classnx-login-illustration.png";
import studentGrowthImage from "../assets/home-slider/student-growth.webp";
import schoolOperationsImage from "../assets/home-slider/school-operations.webp";
import parentConnectionImage from "../assets/home-slider/parent-connection.webp";
import Seo from "./Seo";
import ClassNXLoader from "./ClassNXLoader";
import { getPublicLoginAds, trackPublicLoginAdClick } from "../api/publicContent";
import { useTenant } from "../context/TenantContext";
import { buildTenantSubdomainUrl } from "../utils/tenantState";

const roleFromSession = (response = {}) => {
  const body = response?.data?.data ?? response?.data ?? {};
  return normalizeRole(body?.role || body?.roles?.[0]);
};

const loginSlides = [
  {
    image: loginIllustration,
    eyebrow: "One workspace for every school role",
    title: <>Your entire school day, <span className="text-cyan-300">finally connected.</span></>,
    description: "Move from attendance to academics, fees, communication, and reporting without jumping between disconnected tools.",
  },
  {
    image: schoolOperationsImage,
    eyebrow: "Clarity for school teams",
    title: <>Run daily operations with <span className="text-cyan-300">less friction.</span></>,
    description: "Keep schedules, records, staff workflows, and decisions visible in the same dependable workspace.",
  },
  {
    image: studentGrowthImage,
    eyebrow: "Progress you can act on",
    title: <>Turn school activity into <span className="text-cyan-300">meaningful progress.</span></>,
    description: "Give teachers and leaders a clearer view of learning, participation, and the moments that need attention.",
  },
  {
    image: parentConnectionImage,
    eyebrow: "A stronger school community",
    title: <>Bring every important update <span className="text-cyan-300">closer to home.</span></>,
    description: "Help families stay informed while your team keeps communication organized and easy to follow.",
  },
];

const Login = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [remoteSlides, setRemoteSlides] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const loginReason = new URLSearchParams(location.search).get("reason") || location.state?.reason;
  const authError = new URLSearchParams(location.search).get("auth_error");
  const passwordReset = new URLSearchParams(location.search).get("passwordReset") === "true";

  useEffect(() => {
    let active = true;
    getPublicLoginAds().then((items) => {
      if (!active) return;
      setRemoteSlides(items.filter((item) => item?.title).map((item) => ({
        id: item.id,
        image: item.imageUrl || loginIllustration,
        eyebrow: item.label || "Featured",
        title: item.title,
        description: item.description || "",
        ctaLabel: item.ctaLabel || "Learn more",
        ctaUrl: item.ctaUrl || "",
        sponsored: item.sponsored !== false,
      })));
    });
    return () => { active = false; };
  }, []);

  const visibleSlides = remoteSlides.length ? remoteSlides : loginSlides;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches) return undefined;
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % visibleSlides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [visibleSlides.length]);

  useEffect(() => { setActiveSlide(0); }, [remoteSlides.length]);

  const slide = visibleSlides[activeSlide] || visibleSlides[0];

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      // The server validates the password and establishes its HttpOnly cookie.
      // Do not retain a password or token in browser storage.
      const response = await api.post(
        API_PATHS.auth.login,
        { email: email.trim(), password },
        { withCredentials: true },
      );
      if (response?.data?.success === false) {
        throw new Error(response.data.message || "Login failed.");
      }

      const session = await api.get(API_PATHS.auth.session, { withCredentials: true });
      const role = roleFromSession(session);
      const destination = getDashboardPathForRole(role);
      if (!role || !destination || destination === "/") {
        throw new Error("Login succeeded, but the server did not provide a valid user role.");
      }

      const sessionBody = session?.data?.data ?? session?.data ?? {};
      // Establish the readable CSRF cookie before authenticated background
      // requests such as presence heartbeat and usage logging are enabled.
      await initialiseCsrf();
      window.localStorage.setItem("user", role);
      if (sessionBody?.name) window.localStorage.setItem("currentUserName", sessionBody.name);
      setUser?.(role);
      window.dispatchEvent(new CustomEvent("auth:session-established", {
        detail: { session: sessionBody },
      }));

      // Route users logging in on a common domain (e.g. classnx.com) to their dedicated tenant subdomain (e.g. abc.classnx.com)
      const tenantSubdomain = sessionBody?.subdomain
        || sessionBody?.tenantSubdomain
        || sessionBody?.tenant?.subdomain
        || "";
      const targetSubdomainUrl = buildTenantSubdomainUrl(tenantSubdomain, destination, role);
      if (targetSubdomainUrl) {
        window.location.replace(targetSubdomainUrl);
        return;
      }

      navigate(destination, { replace: true });
    } catch (error) {
      toast.error(error?.message || "Unable to sign in.", { position: "top-center" });
    } finally {
      setPassword("");
      setIsSubmitting(false);
    }
  };

  const {
    activeTenant,
    isSubdomainTenant,
    tenantLogoUrl,
    schoolName: contextSchoolName,
  } = useTenant();

  const isTenantBranded = Boolean(
    isSubdomainTenant ||
    (activeTenant?.subdomain && activeTenant?.brandingEnabled !== false) ||
    activeTenant?.brandingEnabled === true
  );
  const fallbackSubdomainName = isSubdomainTenant && (activeTenant?.subdomain || (typeof window !== "undefined" ? window.location.hostname.split(".")[0] : ""))
    ? (activeTenant?.subdomain || window.location.hostname.split(".")[0]).charAt(0).toUpperCase() + (activeTenant?.subdomain || window.location.hostname.split(".")[0]).slice(1)
    : "";
  const schoolName = (contextSchoolName && contextSchoolName !== "ClassNX" ? contextSchoolName : "")
    || (activeTenant?.schoolName && activeTenant.schoolName !== "ClassNX" ? activeTenant.schoolName : "")
    || (activeTenant?.name && activeTenant.name !== "ClassNX" ? activeTenant.name : "")
    || fallbackSubdomainName
    || (isSubdomainTenant ? "School Campus" : "");
  const schoolLogo = tenantLogoUrl || activeTenant?.logoUrl || activeTenant?.schoolLogoUrl || "";
  const schoolInitials = schoolName
    ? schoolName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
    : "SC";

  const notice = loginReason === "expired"
    ? "Your session expired. Please sign in again."
    : loginReason === "login_required"
      ? "Please sign in to continue."
      : "";

  return (
    <div className="login-experience login-experience--minimal min-h-full px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
      <Seo
        title={isTenantBranded ? `Log In | ${schoolName}` : "Log In | ClassNX"}
        description={isTenantBranded ? `Sign in to your ${schoolName} school management portal.` : "Sign in to your ClassNX school management portal."}
        canonical="/login"
        robots="noindex, follow"
      />
      <main className="login-shell login-shell--minimal mx-auto w-full max-w-md overflow-hidden">
        {isTenantBranded ? (
          <section className="login-intro relative hidden text-white" aria-label={`About ${schoolName}`}>
            <div className="login-desktop-brand relative z-10 flex items-center gap-3.5">
              {schoolLogo ? (
                <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-2xl bg-white p-1.5 shadow-lg shadow-slate-950/20">
                  <img className="h-full w-full object-contain" src={schoolLogo} alt={`${schoolName} logo`} />
                </span>
              ) : (
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/20 text-lg font-black tracking-wider text-white shadow-lg shadow-slate-950/20">
                  {schoolInitials}
                </span>
              )}
              <div>
                <span className="block text-sm font-black tracking-[0.16em] uppercase">{schoolName}</span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-200">Official School Workspace</span>
              </div>
            </div>

            <div className="login-slide-copy relative z-10 max-w-lg mt-8">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-white">
                <FaCheckCircle /> Academic & Campus Access
              </span>
              <h1 className="mt-5 text-[2.6rem] font-black leading-[1.08] tracking-[-0.035em]">
                Welcome to <span className="text-cyan-300">{schoolName}</span>
              </h1>
              <p className="mt-4 max-w-md text-sm leading-6 text-indigo-50/90">
                Access your attendance, timetable, courses, grade reports, fee management, and school announcements in your dedicated campus portal.
              </p>
            </div>

            <div className="relative z-10 mt-10 rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md">
              <div className="flex items-center justify-between text-xs font-semibold text-cyan-100">
                <span>Students · Teachers · Parents · Admin</span>
                <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">Active Campus</span>
              </div>
              <p className="mt-2 text-xs text-indigo-100/75 leading-relaxed">
                Sign in with your school credentials assigned by {schoolName}.
              </p>
            </div>
          </section>
        ) : (
          <section className="login-intro relative hidden text-white" aria-label="About ClassNX">
            <div className="login-desktop-brand relative z-10 flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white shadow-lg shadow-slate-950/20">
                <img className="h-9 w-9 object-contain" src={`${process.env.PUBLIC_URL}/logo.png`} alt="" />
              </span>
              <div><span className="block text-sm font-black tracking-[0.18em]">CLASSNX</span><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-200">The connected school</span></div>
            </div>
            <div key={`copy-${activeSlide}`} className="login-slide-copy relative z-10 max-w-lg">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-white">
                  <FaCheckCircle /> {slide.eyebrow} {slide.sponsored && <span className="ml-1 text-[9px] opacity-60">Sponsored</span>}
                </span>
                <h1 className="mt-5 text-[2.6rem] font-black leading-[1.05] tracking-[-0.04em]">
                  {slide.title}
                </h1>
                <p className="mt-4 max-w-md text-sm leading-6 text-indigo-50/80">
                  {slide.description}
                </p>
                {slide.ctaUrl && (
                  <a href={slide.ctaUrl} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackPublicLoginAdClick(slide.id)} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-cyan-200 hover:text-white">
                    {slide.ctaLabel} <FaArrowRight />
                  </a>
                )}
            </div>

            <div className="relative z-10 mt-7">
              <div className="login-art-frame">
                {visibleSlides.map((item, index) => (
                  <img
                    key={item.image}
                    src={item.image}
                    alt={index === activeSlide ? item.eyebrow : ""}
                    className={`login-slide-image ${index === activeSlide ? "is-active" : ""}`}
                  />
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div className="flex gap-2" role="tablist" aria-label="Login highlights">
                  {visibleSlides.map((item, index) => (
                    <button key={item.eyebrow} type="button" onClick={() => setActiveSlide(index)} className={`login-slide-dot ${index === activeSlide ? "is-active" : ""}`} aria-label={`Show highlight ${index + 1}`} aria-selected={index === activeSlide} role="tab" />
                  ))}
                </div>
                <p className="text-[11px] font-semibold text-blue-100/65">Students · Teachers · Management</p>
              </div>
            </div>
          </section>
        )}

        <section className="login-form-panel bg-white px-6 py-8 sm:px-10 sm:py-10">
            <div className="mb-7 flex justify-end">
              {isTenantBranded ? (
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{schoolName}</span>
              ) : (
                <Link to="/" className="text-sm font-semibold text-slate-500 transition hover:text-slate-900">Back to home</Link>
              )}
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                {isTenantBranded && (
                  <div className="mb-4 flex items-center gap-3">
                    {schoolLogo ? (
                      <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                        <img src={schoolLogo} alt={`${schoolName} logo`} className="h-full w-full object-contain" />
                      </div>
                    ) : (
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 font-extrabold text-white text-base shadow-sm">
                        {schoolInitials}
                      </div>
                    )}
                    <div>
                      <span className="block text-xs font-extrabold uppercase tracking-[0.16em] text-blue-600">
                        {schoolName}
                      </span>
                      <span className="block text-[10px] font-semibold text-slate-400">
                        Campus sign-in
                      </span>
                    </div>
                  </div>
                )}
                {!isTenantBranded && (
                  <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">Secure account access</p>
                )}
                <h1 className="mt-1 text-3xl font-black tracking-[-0.035em] text-slate-950">
                  {isTenantBranded ? `Login to ${schoolName}` : "Welcome back"}
                </h1>
              </div>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-500">
              {isTenantBranded
                ? `Enter your credentials to continue to your ${schoolName} workspace.`
                : "Enter your credentials to continue to your ClassNX workspace."}
            </p>

            {notice && <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{notice}</div>}
            {passwordReset && <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">Password reset successfully. Please log in with your new password.</div>}
            {authError && <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">Sign-in could not be completed. Please try again.</div>}

            <form className="mt-7 space-y-5" onSubmit={handleLogin} aria-busy={isSubmitting}>
          <label className="block text-sm font-medium text-slate-700">
            Email or username
            <span className="relative mt-2 block"><FaEnvelope className="login-field-icon" /><input
              className="login-input w-full border bg-white py-3.5 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 disabled:bg-slate-100"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoFocus
              placeholder="name@school.edu"
              required
              disabled={isSubmitting}
            /></span>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Password
            <span className="relative mt-2 block">
              <input
                className="login-input w-full border bg-white py-3.5 pl-11 pr-11 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 disabled:bg-slate-100"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                placeholder="Enter your password"
                required
                disabled={isSubmitting}
              />
              <FaLock className="login-field-icon" />
              <button
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 transition hover:text-slate-700"
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </span>
          </label>

          <div className="flex items-center justify-end">
            <Link to="/forgot-password" className="text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="login-submit inline-flex min-h-[3.25rem] w-full items-center justify-center gap-3 px-5 py-3 text-sm font-bold text-white transition focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? <><ClassNXLoader variant="inline" size="sm" hideCopy className="login-inline-loader" /> Signing in...</> : <>Sign in <FaArrowRight /></>}
          </button>
            </form>
            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <FaShieldAlt className="shrink-0 text-emerald-600" />
                <span>Protected by a secure encrypted session</span>
              </div>
              <span className="text-[11px] font-medium text-slate-400">Powered by ClassNX</span>
            </div>
        </section>
      </main>
    </div>
  );
};

export default Login;
